import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, RotateCcw, ArrowRight } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import BottleImage from './BottleImage';
import { prefersReducedMotion } from '../lib/motion';

/* ── Quiz data ─────────────────────────────────────────────────────────── */
const QUIZ = [
  {
    q: "What's your signature style?",
    hint: "Be honest — we won't tell.",
    options: [
      { label: "Bold & Iconic",      sub: "Statement scents that command the room",    ids: [4, 7, 6, 11, 3] },
      { label: "Fresh & Effortless", sub: "Clean, sophisticated, everyday luxury",     ids: [15, 9, 2, 13, 18] },
      { label: "Warm & Romantic",    sub: "Soft, sensual, irresistibly close",         ids: [1, 17, 10, 8, 5] },
      { label: "Exotic & Rare",      sub: "Oud, spice, and the extraordinary",         ids: [3, 12, 11, 6, 19] },
    ],
  },
  {
    q: "When do you reach for fragrance?",
    hint: "There's no wrong answer.",
    options: [
      { label: "Date Night",         sub: "Seductive, memorable, unforgettable",       ids: [4, 7, 16, 3, 10] },
      { label: "Daily Confidence",   sub: "Effortless presence, morning to night",     ids: [5, 13, 14, 15, 2] },
      { label: "Grand Occasions",    sub: "Milestone moments, lasting impressions",    ids: [1, 12, 11, 6, 19] },
      { label: "Work & Office",      sub: "Polished, professional, quietly powerful",  ids: [9, 2, 18, 13, 5] },
    ],
  },
  {
    q: "Which scent family speaks to you?",
    hint: "Trust your instincts.",
    options: [
      { label: "Floral & Soft",      sub: "Rose, jasmine, peony, delicate petals",    ids: [8, 19, 10, 20, 17] },
      { label: "Woody & Dark",       sub: "Oud, cedar, smoky, deep mystery",          ids: [6, 7, 11, 3, 4] },
      { label: "Sweet & Warm",       sub: "Vanilla, amber, musk, golden warmth",      ids: [1, 17, 14, 5, 12] },
      { label: "Fresh & Citrus",     sub: "Bergamot, sea breeze, clean radiance",     ids: [15, 9, 2, 18, 13] },
    ],
  },
];

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Rank-weighted tally. Each answer lists its matches best-first, so the top
 * match earns the most points and the last still earns one.
 *
 * Exported for tests — the previous `3 - i` weighting silently handed 0 points
 * to the 4th match and *subtracted* a point from the 5th.
 */
export const scoreAnswer = (scores, ids) => {
  const next = { ...scores };
  ids.forEach((id, i) => {
    next[id] = (next[id] || 0) + (ids.length - i);
  });
  return next;
};

/** Highest score wins; ties fall to the lowest product id for determinism. */
export const pickWinner = (scores, products = PRODUCTS) =>
  products.reduce(
    (best, p) => ((scores[p.id] || 0) > (scores[best.id] || 0) ? p : best),
    products[0]
  );

/* ── Main Quiz Component ────────────────────────────────────────────────── */
const ScentQuiz = () => {
  const [isOpen,  setIsOpen]  = useState(false);
  const [step,    setStep]    = useState(0);   // 0-2 = questions, 3 = result
  const [scores,  setScores]  = useState({});
  const [animOut, setAnimOut] = useState(false);
  const [result,  setResult]  = useState(null);
  const [showBtn, setShowBtn] = useState(false);

  const overlayRef  = useRef(null);
  const panelRef    = useRef(null);
  const triggerRef  = useRef(null);
  const restoreRef  = useRef(null);
  const timerRef    = useRef(null);

  const transitionMs = prefersReducedMotion() ? 0 : 280;

  // Show floating button after scrolling past the hero
  useEffect(() => {
    const onScroll = () => setShowBtn(window.scrollY > window.innerHeight * 0.4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Any pending step transition must not fire after unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const closeQuiz = useCallback(() => {
    setIsOpen(false);
    // Return focus to whatever opened the modal
    const el = restoreRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  }, []);

  // Lock scroll, trap focus, and wire Escape while open
  useEffect(() => {
    if (!isOpen) return undefined;

    restoreRef.current = document.activeElement;

    const { body } = document;
    const prevOverflow = body.style.overflow;
    // Compensate for the vanishing scrollbar so the page doesn't jump
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevPad = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeQuiz();
        return;
      }
      if (e.key !== 'Tab') return;

      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes?.length) return;
      const list = Array.from(nodes).filter(n => n.offsetParent !== null);
      if (!list.length) return;

      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPad;
    };
  }, [isOpen, closeQuiz]);

  const openQuiz = () => {
    setStep(0); setScores({}); setResult(null); setAnimOut(false);
    setIsOpen(true);
  };

  const advance = (fn) => {
    setAnimOut(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fn();
      setAnimOut(false);
    }, transitionMs);
  };

  const handleOption = (ids) => {
    const newScores = scoreAnswer(scores, ids);

    if (step < QUIZ.length - 1) {
      advance(() => { setScores(newScores); setStep(s => s + 1); });
    } else {
      const winner = pickWinner(newScores);
      advance(() => { setScores(newScores); setResult(winner); setStep(QUIZ.length); });
    }
  };

  const handleOrder = () => {
    window.dispatchEvent(new CustomEvent('vael:selectScent', { detail: result.name }));
    closeQuiz();
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }, 200);
  };

  const reset = () => advance(() => { setStep(0); setScores({}); setResult(null); });

  const isResult = step === QUIZ.length;

  return (
    <>
      {/* Floating trigger */}
      <button
        ref={triggerRef}
        onClick={openQuiz}
        data-testid="quiz-trigger-btn"
        aria-haspopup="dialog"
        aria-hidden={!showBtn}
        tabIndex={showBtn ? 0 : -1}
        className="quiz-trigger"
        style={{
          position: 'fixed', bottom: '24px', left: '24px', zIndex: 400,
          display: 'flex', alignItems: 'center', gap: '8px',
          fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px',
          letterSpacing: '2.5px', textTransform: 'uppercase',
          color: 'var(--gold)', background: 'rgba(10,10,8,0.9)',
          border: '1px solid rgba(201,169,110,0.4)',
          padding: '13px 18px', cursor: 'pointer', borderRadius: '2px',
          backdropFilter: 'blur(12px)',
          opacity: showBtn ? 1 : 0,
          transform: showBtn ? 'translateY(0)' : 'translateY(12px)',
          pointerEvents: showBtn ? 'auto' : 'none',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M5 1L6.18 3.4L8.9 3.76L6.95 5.66L7.47 8.38L5 7.1L2.53 8.38L3.05 5.66L1.1 3.76L3.82 3.4L5 1Z" fill="#C9A96E" />
        </svg>
        Find Your Scent
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div
          ref={overlayRef}
          data-testid="quiz-modal"
          onMouseDown={e => { if (e.target === overlayRef.current) closeQuiz(); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(10,10,8,0.96)', backdropFilter: 'blur(24px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px', overflowY: 'auto',
          }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-heading"
            tabIndex={-1}
            style={{
              width: '100%', maxWidth: '680px',
              background: 'var(--deep-warm-black)',
              border: '1px solid rgba(201,169,110,0.15)',
              borderRadius: '2px', padding: 'clamp(32px, 5vw, 56px)',
              position: 'relative', outline: 'none',
            }}
          >
            {/* Close */}
            <button
              onClick={closeQuiz}
              data-testid="quiz-close-btn"
              aria-label="Close the scent finder"
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: 'var(--grey)', cursor: 'pointer', padding: '10px', lineHeight: 0 }}
            >
              <X size={18} strokeWidth={1.5} aria-hidden="true" />
            </button>

            {/* Progress bar */}
            {!isResult && (
              <div style={{ marginBottom: '36px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold)' }}>
                    Find Your Scent
                  </span>
                  <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '2px', color: 'var(--grey)' }}>
                    {step + 1} / {QUIZ.length}
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={step + 1}
                  aria-valuemin={1}
                  aria-valuemax={QUIZ.length}
                  aria-label="Quiz progress"
                  style={{ height: '1px', background: 'rgba(201,169,110,0.1)', position: 'relative' }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', background: 'var(--gold)', transition: 'width 0.5s ease', width: `${((step + 1) / QUIZ.length) * 100}%` }} />
                </div>
              </div>
            )}

            {/* Content */}
            <div
              className="quiz-panel"
              style={{
                opacity: animOut ? 0 : 1,
                transform: animOut ? 'translateY(12px)' : 'translateY(0)',
              }}
            >
              {/* Questions */}
              {!isResult && (
                <>
                  <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '15px', color: 'var(--grey)', letterSpacing: '1px', marginBottom: '10px' }}>
                    {QUIZ[step].hint}
                  </p>
                  <h2 id="quiz-heading" style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(18px, 3vw, 26px)', letterSpacing: '3px', color: 'var(--cream)', fontWeight: 400, marginBottom: '36px', lineHeight: 1.3 }}>
                    {QUIZ[step].q}
                  </h2>

                  <div className="quiz-options">
                    {QUIZ[step].options.map((opt, i) => (
                      <button
                        key={opt.label}
                        onClick={() => handleOption(opt.ids)}
                        data-testid={`quiz-option-${i}`}
                        className="quiz-option"
                      >
                        <span style={{ display: 'block', fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '13px', letterSpacing: '2.5px', color: 'var(--gold-light)', marginBottom: '8px', textTransform: 'uppercase' }}>
                          {opt.label}
                        </span>
                        <span style={{ display: 'block', fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '15px', color: 'var(--grey)', lineHeight: 1.5 }}>
                          {opt.sub}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Result */}
              {isResult && result && (
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '8px' }}>
                    Your Perfect Scent
                  </span>
                  <h2 id="quiz-heading" style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(22px, 4vw, 34px)', letterSpacing: '5px', color: 'var(--cream)', fontWeight: 400, marginBottom: '6px' }}>
                    {result.name}
                  </h2>
                  <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '16px', color: 'var(--grey)', marginBottom: '28px' }}>
                    Inspired by {result.inspired_by}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <BottleImage product={result} height={230} eager />
                  </div>

                  {/* Notes */}
                  <div className="quiz-notes">
                    {[['Top', result.notes.top], ['Heart', result.notes.heart], ['Base', result.notes.base]].map(([label, val]) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '10px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold-dark)', marginBottom: '6px' }}>{label}</div>
                        <div style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '14px', color: 'var(--cream)', lineHeight: 1.5 }}>{val}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handleOrder} className="btn-gold" data-testid="quiz-order-btn"
                      style={{ background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      Order This Scent <ArrowRight size={12} aria-hidden="true" />
                    </button>
                    <button onClick={reset} data-testid="quiz-retake-btn" className="quiz-retake">
                      <RotateCcw size={12} aria-hidden="true" /> Retake Quiz
                    </button>
                  </div>

                  <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '13px', color: 'var(--grey-dim)', marginTop: '20px' }}>
                    $100 CAD · 50mL EDP · 100mL available upon request
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScentQuiz;
